import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { SourcedItem } from 'src/app/shared/models/app-models/SourcingOutcome.model';
import { BaseTableColumn } from 'src/app/shared/models/app-models/BaseTableColumn.model';
import { Store } from '@ngrx/store';
import {
  DropDownOptions,
  ExtendedMaterialSubGroup,
  ExtendedOEM,
  StoreDropdowns,
} from 'src/app/shared/models/app-models';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { StatusData,SummaryDropdown } from 'src/app/modules/rfq-summary/rfq-summary.component';
import { FormControl } from '@angular/forms';
import { MPMMSInformation } from 'src/app/shared/models/app-models/MPMMSInformation.model';

@Component({
  selector: 'app-item-selection-listing',
  templateUrl: './item-selection-listing.component.html',
  styleUrls: ['./item-selection-listing.component.scss'],
  providers: [
    DatePipe
  ]
})
export class ItemSelectionListingComponent implements OnChanges {
  destroy$: Subject<boolean> = new Subject<boolean>();
  @Output() selected = new EventEmitter<SourcedItem[]>();
  @Output() allItemsHavePoChange = new EventEmitter<boolean>();
  @Input() selectionMode: boolean = false;
  @Input() list: SourcedItem[] = [];
  @Input() type: string | null = null;
  @Input() selectedReasonForAbort: DropDownOptions | null = null;
  @Input() mpMmsInformation: MPMMSInformation[] = []
  @Input() preselect: SourcedItem[] = [];

  pageSizeOptions: number[] = [5, 10, 20, 50];
  isLoadingData: boolean = true;
  allItemsHavePo: boolean = false;
  @Input() statusData!: StatusData;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  pageEvent: PageEvent | undefined;
  materialSubGroups: ExtendedMaterialSubGroup[] = [];
  OEMList: ExtendedOEM[] = [];
  dataSource: MatTableDataSource<SourcedItem> = new MatTableDataSource<SourcedItem>([]);
  RoleTestControl = new FormControl('PE');
  private selection = new BehaviorSubject<SourcedItem[]>([]);
  selection$ = this.selection.asObservable();

  groupedMpMmsData: { [key: number]: MPMMSInformation[] } = {};
  RFQAbortReasons: SummaryDropdown[] = [];
  itemColumns: BaseTableColumn<SourcedItem>[] = [
    {
      columnDef: "Select",
      header: '',
      cell: () => '',
    },
    {
      columnDef: 'PurchaseItemNumber',
      header: 'MP Item No.',
      cell: (element: SourcedItem) => `${element.PurchaseItemNumber}`,
    },
    {
      columnDef: 'RFQItemNumber',
      header: 'RFQ Item No.',
      cell: (element: SourcedItem) => `${element.RFQItemNumber}`,
    },
    {
      columnDef: 'ReasonForAbort',
      header: 'Reason for aborting',
      cell: (element: SourcedItem) => element.ReasonForAbort ? `${element.ReasonForAbort}` : "Not yet selected",
    },
    {
      columnDef: 'Quantity',
      header: 'Outstanding Qty',
      cell: (element: SourcedItem) => `${element.Quantity}`,
    },
    {
      columnDef: 'MaterialSubGroupId',
      header: 'Material sub-group',
      cell: (element: SourcedItem) => this.formatMaterialSubGroupId(element),
    },
    {
      columnDef: 'ItemDescription',
      header: 'Item description',
      cell: (element: SourcedItem) => `${element.ItemDescription}`,
    },
    {
      columnDef: 'Vendor',
      header: 'Vendor',
      cell: (element: SourcedItem) => element.Vendor ? `${element.Vendor}` : '-',
    },
    {
      columnDef: 'PONumber',
      header: 'PO No.',
      cell: (element: SourcedItem) => element.PONumber ? `${element.PONumber}` : `-`,
    },
    {
      columnDef: 'PODocStatus',
      header: 'PO status',
      cell: (element: SourcedItem) => element.PODocStatus ? `${element.PODocStatus}` : `-`,
    },
    {
      columnDef: 'AwardedQuantity',
      header: 'Awarded Qty',
      cell: (element: SourcedItem) => element.AwardedQuantity ? `${element.AwardedQuantity}` : `-`,
    },
    {
      columnDef: 'RFQQuantity',
      header: 'RFQ Qty',
      cell: (element: SourcedItem) => element.RFQQuantity ? `${element.RFQQuantity}` : `-`,
    },
    {
      columnDef: 'UnitOfMeasurement',
      header: 'UOM',
      cell: (element: SourcedItem) => `${element.UnitOfMeasurement}`,
    },
    {
      columnDef: 'BrandOEMName',
      header: 'OEM/Brand',
      cell: (element: SourcedItem) => {
        let value =  `-`;

        if(element.BrandOEMName) {
          value = this.OEMList.find((item) => item.id == element.BrandOEMName)?.value ?? value;
        }

        return value;
      },
    },
    {
      columnDef: 'PartModelNumber',
      header: 'Part no.',
      cell: (element: SourcedItem) => element.PartModelNumber ? `${element.PartModelNumber ?? "-"}` : `-`,
    },
    {
      columnDef: 'PlannedDeliveryDate',
      header: 'Planned Delivery Date',
      cell: (element: SourcedItem) => this.formatPlannedDeliveryDate(element),
    },
    {
      columnDef: 'SelectedReasonForAbort',
      header: 'Reason for aborting',
      cell: (element: SourcedItem) => this.formatAbortReasonName(element),
    }
  ];
  // initialized without selection
  displayedColumns = this.itemColumns.filter((item) => item.columnDef != "Select").map(c => c.columnDef);

  constructor(private datePipe: DatePipe, private store: Store<{ dp: StoreDropdowns }>) {
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.materialSubGroups = dp.MaterialSubGroup as ExtendedMaterialSubGroup[];
        this.RFQAbortReasons=dp.RFQAbortReasons;
        this.OEMList = dp.OEM as ExtendedOEM[];
      });
  }

  ngOnInit(): void {
    this.selection$.subscribe((items: SourcedItem[]) => {
      this.selected.emit(items);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["preselect"] && changes["preselect"].previousValue != changes["preselect"].currentValue && changes["preselect"].currentValue != this.selection.value) {
      this.selection.next(changes["preselect"].currentValue);
    }
    if ((changes["type"] && changes["type"].previousValue != changes["type"].currentValue)
      || (changes["selectionMode"] && changes["selectionMode"].previousValue != changes["selectionMode"].currentValue)) {
      this.updateDisplayedColumn();
    }

    if (changes['list'] || changes['mpMmsInformation']) {
      this.populateDataSource();
    }
  }

  allItemsHavePO() {
    this.allItemsHavePo = this.dataSource.data.every(item => item.PONumber && item.PONumber !== '-');
    this.allItemsHavePoChange.emit(this.allItemsHavePo);
    // this.allItemsHavePoChange.emit(false); //change this to test complete dialog in the future
    // console.log("ITEM SELECTION LISTING HERE this.allItemsHavePO", this.allItemsHavePo);
  }

  populateDataSource() {
    // console.log("THIS.LIST", this.list);
    // console.log("THIS.MPMMSInfo", this.mpMmsInformation);

    if (this.list && this.mpMmsInformation) {
      // Flatten the nested array structure
      const flattenedMmsData = this.mpMmsInformation.flat();

      // Group MMS data by ItemNumber
      const groupedMpMmsData = flattenedMmsData.reduce((acc, mms) => {
        if (!acc[mms.CopiedFromItemNumber]) {
          acc[mms.CopiedFromItemNumber] = [];
        }
        acc[mms.CopiedFromItemNumber].push(mms);
        return acc;
      }, {} as { [key: number]: any[] });

      this.groupedMpMmsData = groupedMpMmsData;
      console.log("this.groupedMpMmsData", this.groupedMpMmsData);


      // Merge 'list' with grouped MMS data
      const mergedData = this.list.map(item => {
        let mmsInfos = groupedMpMmsData[item.PurchaseItemId] || [];

        // Format Vendor and PO Number data for display
        const vendorInfo = mmsInfos.map(mms => `${mms?.VendorName?.Name.trim() || ''}`).join('<br>');
        const poNumbers = mmsInfos.map(mms => `${mms?.DocumentNumber || ''}`).join('<br>');
        const awardedQty = mmsInfos.map(mms => `${mms?.Quantity || ''}`).join('<br>');
        const poDocStatus = mmsInfos.map(mms => `${mms?.ProcessStatus || ''}`).join('<br>');

        // Determine Quantity and AwardedQuantity
        const totalAwardedQty = mmsInfos.reduce((sum, mms) => sum + (mms.Quantity || 0), 0);

        return {
          ...item,
          Vendor: vendorInfo || '-',
          PONumber: poNumbers || '-',
          Quantity: (item?.Quantity || 0) - totalAwardedQty,
          RFQQuantity: item?.Quantity || 0,
          AwardedQuantity: awardedQty || '0',
          PODocStatus: poDocStatus || '-'
        };
      });

      // Set the merged data as the data source for the table
      this.dataSource.data = mergedData;
      console.log("MERGED DATA", this.dataSource.data);

      // Attach paginator after setting data
      setTimeout(() => { 
        this.dataSource.paginator = this.paginator;
        this.allItemsHavePO();
      }, 0);

    } else {
      console.warn("List or MPMMS Information is missing.");
    }
  }

  updateDisplayedColumn() {
    this.displayedColumns = this.itemColumns.filter((item) => {
      if (item.columnDef == "Select") {
        return this.selectionMode;
      } else if (item.columnDef == "ReasonForAbort") {
        return this.type == "abort";
      } else if(item.columnDef == "SelectedReasonForAbort"){
        return this.type !== "abort";
      }
      else {
        return true;
      }
    }).map((c) => c.columnDef);
  }

  formatMaterialSubGroupId(element: SourcedItem): any {
    const subGropuname = this.materialSubGroups?.find((subGroup: any) => subGroup.Code === element.MaterialSubGroupId)?.value;
    return subGropuname;
  }

  public formatPlannedDeliveryDate(element: SourcedItem): string {
    if (element.PlannedDeliveryDate) {
      const date = element.PlannedDeliveryDate;
      return this.datePipe.transform(date, 'dd/MM/yyyy') || '';
    }
    return '';
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public get isIndeterminate(): boolean {
    return this.list && this.selection.value && this.selection.value.length > 0 && this.selection.value.length !== this.list.length;
  }

  onRecordClick(row: SourcedItem) {
    if(this.selection.value.some((pre) => pre.Id == row.Id)) {
      this.selection.next(this.selection.value.filter((pre) => pre.Id != row.Id));
    } else {
      this.selection.next(this.list.filter((item) => item.Id == row.Id || this.selection.value.some((pre) => pre.Id == item.Id)));
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  public get isAllSelected() {
    return this.selection.value.length === this.list.length;
  }

  isSelected(row: SourcedItem) {
    return Boolean(this.selection.value.find((item) => row.Id == item.Id));
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected) {
      this.selection.next([]);
      return;
    }
    this.selection.next(this.list);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: SourcedItem): string {
    if (!row) {
      return `${this.isAllSelected ? 'deselect' : 'select'} all`;
    }
    return `${this.isSelected(row) ? 'deselect' : 'select'} row ${row.ItemDescription}`;
  }
  
  isRowChecked(row: SourcedItem): boolean {
    return this.selection.value.some((item: SourcedItem) => item.Id == row.Id);
  }

  public formatAbortReasonName(element: SourcedItem): string {
    if (element.AbortReasonId) {
      const reasonName = this.RFQAbortReasons?.find((option: any) => option.Id === element.AbortReasonId)?.Name;
      return reasonName || '-';
    }
    return '-';
  }

}
